import { formatInTimeZone } from 'date-fns-tz';

export const CALENDAR_PROVIDERS = [
  { id: 'apple', name: 'Apple' },
  { id: 'google', name: 'Google' },
  { id: 'outlookcom', name: 'Outlook.com' },
  { id: 'yahoo', name: 'Yahoo' },
  // Generic download, last: the same signed file over plain https, which any
  // calendar application can open.
  { id: 'ical', name: 'iCal' },
];

// Apple and iCal have no compose url, so they consume the ics file. Apple gets
// it over webcal:// so the OS hands it straight to the calendar app.
const FILE_PROVIDERS = new Set(['apple', 'ical']);

function toCompactUtc(iso) {
  return `${iso
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '')
    .replace(/Z$/, '')}Z`;
}

/**
 * Neither Yahoo nor Outlook.com honours a trailing Z: both render the timestamp
 * as the viewer's own wall clock, so sending UTC shows the wrong time to
 * everyone. Yahoo has no timezone parameter at all; Outlook.com documents the Z
 * as UTC but ignores it (2026-09-06: a 02:06Z start opened as 02:06 in a GMT+7
 * calendar). An explicit offset is no alternative, because Outlook decodes the
 * %2B of a positive offset as a space and then drops the time.
 * https://interactiondesignfoundation.github.io/add-event-to-calendar-docs/services/yahoo.html
 */
const YAHOO_WALL_CLOCK = "yyyyMMdd'T'HHmmss";
const OUTLOOK_WALL_CLOCK = "yyyy-MM-dd'T'HH:mm:ss";

function toWallClock(iso, timezone, pattern) {
  return formatInTimeZone(new Date(iso), timezone || 'Etc/UTC', pattern);
}

function withParams(base, params) {
  const url = new URL(base);

  Object.entries(params).forEach(([key, value]) => {
    if (value) url.searchParams.set(key, value);
  });

  return url.toString();
}

const OUTLOOK_PARAMS = event => ({
  path: '/calendar/action/compose',
  rru: 'addevent',
  subject: event.title,
  startdt: toWallClock(event.start, event.timezone, OUTLOOK_WALL_CLOCK),
  enddt: toWallClock(event.end, event.timezone, OUTLOOK_WALL_CLOCK),
  body: event.description,
  location: event.location,
});

const builders = {
  google: event =>
    withParams('https://calendar.google.com/calendar/render', {
      action: 'TEMPLATE',
      text: event.title,
      dates: `${toCompactUtc(event.start)}/${toCompactUtc(event.end)}`,
      ctz: event.timezone,
      details: event.description,
      location: event.location,
    }),

  yahoo: event =>
    withParams('https://calendar.yahoo.com/', {
      v: '60',
      title: event.title,
      st: toWallClock(event.start, event.timezone, YAHOO_WALL_CLOCK),
      et: toWallClock(event.end, event.timezone, YAHOO_WALL_CLOCK),
      desc: event.description,
      in_loc: event.location,
    }),

  outlookcom: event =>
    withParams('https://outlook.live.com/calendar/0/deeplink/compose', OUTLOOK_PARAMS(event)),
};

export function getCalendarLink(providerId, { event, icsUrl }) {
  if (!event) return null;

  if (FILE_PROVIDERS.has(providerId)) {
    if (!icsUrl) return null;

    // webcal:// makes the OS hand the file straight to the calendar app.
    return providerId === 'apple' ? icsUrl.replace(/^https?:\/\//, 'webcal://') : icsUrl;
  }

  const build = builders[providerId];

  return build ? build(event) : null;
}
